import { useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { updateOrderWithEntities } from 'actions/entities/orders/orders';
import { fetchUserPayments } from 'actions/entities/payments/payments';
import { fetchOfferFromOrder } from 'actions/entities/recipients/fetchOffer';
import { fetchPaymentsRecipient } from 'actions/entities/recipients/recipients';
import { trackCheckMarkup, trackPaymentOption } from 'actions/tracker/tracker';
import { clearOfferFields, setFieldsValues } from 'actions/ui/ui';
import { Autocomplete } from 'components/Autocomplete/Autocomplete';
import { OptionList } from 'components/PaymentOptions/components/OptionList/OptionList';
import { PlanSummaryModal } from 'components/RecurringReceivables/components/PlanSummaryModal/PlanSummaryModal';
import { SpinnerOverlay } from 'components/SpinnerOverlay/SpinnerOverlay';
import { UserAccessModal } from 'components/UserAccessModal/UserAccessModal';
import { useWizard } from 'components/Wizard/components/context';
import type { FetchRecurringOffers } from 'components/india/steps/PaymentOptionsStep';
import { IS_RECURRING_FIELD, SCHEDULE_FIELD } from 'constants/fields';
import { ExclamationCircle as ExclamationCircleIcon } from 'icons/ExclamationCircle';
import type { RecurringOffersMetadata } from 'models/offer';
import type { RootState } from 'reducers/types';
import { isLoggedIn } from 'selectors/entities/authentication/authentication';
import { getCurrency } from 'selectors/entities/currencies/currencies';
import {
  getRecurringOffers,
  getRecurringOffersMetadata,
} from 'selectors/entities/offers/offers';
import { getRecipient } from 'selectors/entities/recipient/recipient';
import { isFetching as isFetchingSelector, isReadOnly } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { useTranslations } from 'utils/translations/useTranslations';
import { NoOptions } from './NoOptions/NoOptions';

import './RecurringPayment.scss';

const OFFER_FIELD = 'offer';
const INTERVAL_FIELD = 'interval';
const OFFERS_ENTITY = 'offers';

type RecurringPaymentProps = ConnectedProps<typeof connector> & {
  fetchRecurringOffers: FetchRecurringOffers;
  metadata?: RecurringOffersMetadata;
};

function RecurringPaymentComponent({
  clearOfferFields,
  fetchOffer,
  fetchPaymentsRecipient,
  fetchRecurringOffers,
  fetchUserPayments,
  getCurrency,
  isFetching,
  isLoggedIn,
  metadata,
  options = [],
  readonly,
  recipient,
  setFieldsValues,
  trackCheckMarkup,
  trackPaymentOption,
  updateOrderWithEntities,
}: RecurringPaymentProps) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const {
    numberOfInstallments = 0,
    configuredInstallments: installments = [],
    schedule,
  } = metadata || {};

  const canChooseInstallments = installments.length > 1;

  const selectedInstallment = `${numberOfInstallments}`;

  const [selectedOptionId, setSelectedOptionId] = useState<string>();
  const [alreadySelected, setAlreadySelected] = useState(false);
  const [selectingOffer, setSelectingOffer] = useState(false);
  const { goToNextStep } = useWizard();
  const noOptions = options.length === 0;
  const installmentOptions = installments.map(installment => ({
    label: `${installment}`,
    value: `${installment}`,
  }));

  const _options = options.map(option => ({
    ...option,
    interval: Number(selectedInstallment),
    chargeDay: schedule === 'pay_now' ? undefined : parseInt(schedule, 10),
  }));

  async function selectOption(id: string) {
    if (readonly || alreadySelected) {
      return;
    }

    setSelectingOffer(true);
    setAlreadySelected(true);
    await clearOfferFields();

    await Promise.all([
      setFieldsValues({
        [OFFER_FIELD]: id,
        [INTERVAL_FIELD]: selectedInstallment,
        [IS_RECURRING_FIELD]: true,
        [SCHEDULE_FIELD]: schedule,
      }),
      fetchOffer(id),
    ]);

    setSelectingOffer(false);
    updateOrderWithEntities(OFFERS_ENTITY).then(goToNextStep);
  }

  async function onUserLogIn() {
    await Promise.all([fetchUserPayments(), fetchPaymentsRecipient()]);
  }

  function closeModal() {
    setIsModalVisible(false);
  }

  function handleOnLoginSuccess() {
    onUserLogIn();
    closeModal();
    selectedOptionId && selectOption(selectedOptionId);
  }

  function handleOnSignUpSuccess() {
    closeModal();
    selectedOptionId && selectOption(selectedOptionId);
  }

  const onOptionSelect = (id: string) => {
    if (!isLoggedIn) {
      setSelectedOptionId(id);
      setIsModalVisible(true);
      return;
    }

    selectOption(id);
  };

  const onInstallmentChange = (value: string) => {
    dispatch(fetchRecurringOffers({ installments: Number(value) }));
  };

  const [isPlanSummaryModalOpen, setIsPlanSummaryModalOpen] = useState(false);

  return (
    <>
      {isFetching || selectingOffer ? (
        <SpinnerOverlay />
      ) : noOptions ? (
        <NoOptions />
      ) : (
        <div className="PaymentOptions">
          <UserAccessModal
            isOpen={isModalVisible}
            onClose={closeModal}
            onLoginSuccess={handleOnLoginSuccess}
            onSignUpSuccess={handleOnSignUpSuccess}
          />
          <div>
            {canChooseInstallments ? (
              <>
                <h2 className="PaymentPlan-title">
                  {i18n.t('paymentPlan.title')}
                </h2>
                <p
                  className="PaymentPlan-description"
                  dangerouslySetInnerHTML={createMarkup(
                    i18n.t('paymentPlan.description'),
                  )}
                />
                <Autocomplete
                  className="PaymentPlan-installments"
                  name="installments"
                  onChange={onInstallmentChange}
                  options={installmentOptions}
                  data-testid="installmentsAutocomplete"
                  selected={selectedInstallment}
                  required={true}
                >
                  <Autocomplete.Input
                    data-testid="installmentsDropdown"
                    placeholder={
                      i18n.t('paymentPlan.installments.label') as string
                    }
                  />
                  <Autocomplete.Options />
                </Autocomplete>
              </>
            ) : (
              <div className="mb-10 bg-[#e4f2ff] px-4 py-3 sm:mb-6">
                <div className="flex items-start sm:items-center">
                  <div className="mt-0.5 shrink-0 sm:mt-0">
                    <ExclamationCircleIcon
                      aria-hidden="true"
                      className="block size-4 text-[#116EBB]"
                    />
                  </div>
                  <div className="ml-2 flex-1 space-y-2 sm:flex sm:items-center sm:justify-between sm:space-x-4 sm:space-y-0">
                    <p
                      className="m-0 text-sm text-gray-950"
                      dangerouslySetInnerHTML={createMarkup(
                        i18n.t('recurringPayment.recipientPlan', {
                          recipientName: `${recipient.name}`,
                          installments: `${numberOfInstallments}`,
                        }),
                      )}
                    />

                    <div className="flex items-center">
                      <button
                        type="button"
                        className="cursor-pointer whitespace-nowrap rounded border border-gray-300 bg-white px-4 py-1.5 text-base font-normal text-blue-600 shadow-sm"
                        onClick={() => setIsPlanSummaryModalOpen(true)}
                      >
                        {i18n.t('recurringPayment.viewPlanSummary')}
                      </button>
                    </div>
                  </div>
                </div>
                <PlanSummaryModal
                  isOpen={isPlanSummaryModalOpen}
                  onClose={() => setIsPlanSummaryModalOpen(false)}
                />
              </div>
            )}
          </div>
          <div>
            <OptionList
              a11yMessage={i18n.t('paymentOptions.localOptions') as string}
              getCurrency={getCurrency}
              onOptionSelect={onOptionSelect}
              options={_options}
              readonly
              trackCheckMarkup={trackCheckMarkup}
              trackPaymentOption={trackPaymentOption}
            />
          </div>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  getCurrency: (code: string) => getCurrency(state, code),
  isFetching:
    isFetchingSelector(state, 'offers') || isFetchingSelector(state, 'order'),
  isLoggedIn: isLoggedIn(state),
  options: getRecurringOffers(state),
  readonly: isReadOnly(state, 'offer'),
  recipient: getRecipient(state),
  metadata: getRecurringOffersMetadata(state),
});

const mapDispatchToProps = {
  clearOfferFields,
  fetchOffer: fetchOfferFromOrder,
  fetchPaymentsRecipient,
  fetchUserPayments,
  setFieldsValues,
  trackCheckMarkup,
  trackPaymentOption,
  updateOrderWithEntities,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const RecurringPayment = connector(RecurringPaymentComponent);

export { RecurringPayment };
